import React, { useState, useRef, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import LayoutIcon from "src/components/icons/miscs/layout";
import TrashIconTwo from "src/assets/images/TrashTwo.svg";
import AddIcon from "src/components/icons/common/add-icon";
import DragIconTwo from "src/assets/images/DragIconTwo.svg";


const ItemType = {
  ITEM: "item",
};

interface ReportSection {
  title: string;
  summary: string | null;
  body: string | null;
  subsections: Record<string, ReportSection>;
}

interface ReportData {
  title: string;
  sections: Record<string, ReportSection>;
}

interface DraggableItemProps {
  itemKey: string;
  index: number;
  section: ReportSection;
  moveItem: (from: number, to: number) => void;
  onDelete: (key: string) => void;
}

/* ---------------- DRAGGABLE ROOT SECTION ---------------- */

const DraggableItem: React.FC<DraggableItemProps> = ({
  itemKey,
  index,
  section,
  moveItem,
  onDelete,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLImageElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemType.ITEM,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemType.ITEM,
    hover(dragged: { index: number }) {
      if (dragged.index !== index) {
        moveItem(dragged.index, index);
        dragged.index = index;
      }
    },
  });

  drag(dragHandleRef);
  drop(ref);

  return (
    <div className="flex gap-1">
     {/* <img
        ref={dragHandleRef}
        src={DragIconTwo}
        alt="drag"
        className="cursor-grab mt-1 opacity-70 hover:opacity-100"
      /> */}
    <div
      ref={ref}
      className={`flex gap-2 mb-3 p-2 rounded-lg border ${
        isDragging ? "bg-purple-50 shadow-md" : "bg-white"
      }`}
    >
     

      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-semibold">{section.title}</h3>
          <button onClick={() => onDelete(itemKey)}>
            <img src={TrashIconTwo} className="w-2 h-2" />
          </button>
        </div>

        {section.summary && (
          <p className="text-sm text-gray-700 mb-1">
            <strong>Summary:</strong> {section.summary}
          </p>
        )}

        {section.body && (
          <p className="text-sm text-gray-600 whitespace-pre-line">
            {section.body}
          </p>
        )}

        {/* SUBSECTIONS */}
        {section.subsections &&
          Object.entries(section.subsections).map(([subKey, sub]) => (
            <div
              key={subKey}
              className=" mt-3"
            >
              <h4 className="font-medium">{sub.title}</h4>
              {sub.summary && (
                <p className="text-sm text-gray-700">{sub.summary}</p>
              )}
              {sub.body && (
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {sub.body}
                </p>
              )}
            </div>
          ))}
      </div>
    </div>
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

interface Props {
  reportData: ReportData;
  disabled?: boolean;
  onChange?: (data: ReportData) => void;
}

const AnythingReportTemplate: React.FC<Props> = ({
  reportData,
  disabled = false,
  onChange,
}) => {
  const [sections, setSections] = useState(reportData.sections);
  const [open, setOpen] = useState(true);

  /* ADD SECTION STATE */
  const [showAdd, setShowAdd] = useState(false);
  const [keyVal, setKeyVal] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    onChange?.({ ...reportData, sections });
  }, [sections]);

  /* -------- DRAG MOVE (ROOT ONLY) -------- */
  const moveItem = (from: number, to: number) => {
    const entries = Object.entries(sections);
    const [moved] = entries.splice(from, 1);
    entries.splice(to, 0, moved);
    setSections(Object.fromEntries(entries));
  };

  /* -------- DELETE ROOT SECTION -------- */
  const handleDelete = (key: string) => {
    const copy = { ...sections };
    delete copy[key];
    setSections(copy);
  };

  /* -------- ADD ROOT SECTION -------- */
  const handleAdd = () => {
    if (!keyVal || !title || !summary) return;

    setSections((prev) => ({
      ...prev,
      [keyVal]: {
        title,
        summary,
        body,
        subsections: {},
      },
    }));

    setKeyVal("");
    setTitle("");
    setSummary("");
    setBody("");
    setShowAdd(false);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-[90vh] py-4 border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
        <div className="flex justify-between items-center mb-2 border-b border-gray-300 pb-4 px-4">
          <h2 className="text-lg font-semibold">
            {reportData.title}
          </h2>
          <button onClick={() => setOpen(!open)}>
            <LayoutIcon />
          </button>
        </div>

        {open && (
          <div className="mx-3">
            {Object.entries(sections).map(([key, section], index) => (
              <DraggableItem
                key={key}
                itemKey={key}
                index={index}
                section={section}
                moveItem={moveItem}
                onDelete={handleDelete}
              />
            ))}

            {!disabled && (
              <div className="mt-4 border rounded-lg p-2">
                {showAdd ? (
                  <>
                    <input
                      className="border p-1 w-full mb-1"
                      placeholder="Section key (e.g. 7)"
                      value={keyVal}
                      onChange={(e) => setKeyVal(e.target.value)}
                    />
                    <input
                      className="border p-1 w-full mb-1"
                      placeholder="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    <textarea
                      className="border p-1 w-full mb-1"
                      placeholder="Summary"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                    />
                    <textarea
                      className="border p-1 w-full mb-1"
                      placeholder="Body (optional)"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                    />
                    <button
                      onClick={handleAdd}
                      className="text-purple-700 flex items-center gap-1"
                    >
                      <AddIcon /> Add Section
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowAdd(true)}
                    className="flex items-center gap-1 text-purple-700"
                  >
                    <AddIcon /> Add Section
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default AnythingReportTemplate;
